import { z } from 'zod';
import { BullMQPayloadBaseSchema, JobRedisStateBaseSchema } from './base.js';

/**
 * ocr-extract — on-demand (lazy backfill) OCR text extraction of a single piece
 * that has no extracted markdown stored yet in `case_document_ocr`.
 *
 * Use case: the « voir le texte extrait » viewer toggle. Pieces synthesized
 * before STAGE 2.5 of synthesis_service (migration 124) have no persisted
 * markdown, so the toggle enqueues this job the first time it is opened. The
 * frontend POSTs /api/documents/:documentId/ocr-text/extract, then polls
 * GET /api/documents/:documentId/ocr-text until `exists:true`.
 *
 * No Redis state is read by the frontend: the extracted text lives in
 * `case_document_ocr` (served by the GET route). The processor delegates to
 * `SynthesisService.extractAndPersistOcr` (same extraction chain as STAGE 2.5,
 * zero duplication).
 *
 * `.passthrough()` during the DEBT-069 migration window (cf. workers/CLAUDE.md A3).
 */

/**
 * BullMQ payload enqueued by the POST .../ocr-text/extract route.
 *
 * `jobId` is overridden to optional (the base makes it required): the enqueue
 * route sends only `{ documentId, cabinetId, requestId }` and never calls
 * `jobManager.createJob`, so there is no `job:{jobId}` Redis record and no jobId
 * in `data` — same shape as the generate-case-summary payload.
 *
 * `documentId` identifies the piece; `cabinetId` is propagated for cost
 * tracking / ALS (access is already verified route-side via verifyDocumentAccess).
 */
export const OcrExtractPayloadSchema = BullMQPayloadBaseSchema.extend({
  jobId: z.string().optional(),
  documentId: z.string().uuid(),
  cabinetId: z.string().uuid().optional(),
}).passthrough();
export type OcrExtractPayload = z.infer<typeof OcrExtractPayloadSchema>;

/**
 * Redis job state — purely infra (status / error). No result field: the
 * extracted text is persisted to `case_document_ocr`, not to Redis.
 */
export const OcrExtractJobDataSchema = JobRedisStateBaseSchema.extend({
  documentId: z.string().uuid().optional(),
}).passthrough();
export type OcrExtractJobData = z.infer<typeof OcrExtractJobDataSchema>;
